import { from } from 'rxjs'
import { concatMap, map, toArray } from 'rxjs/operators'

import { Component } from '@angular/core'
import { Feature } from '@classes/Feature'
import { FeatureService } from '@services/feature.service'
import { SceneService } from '@services/scene.service'
import { ToastService } from '@services/toast.service'

interface PropertyOption {
  key: string
  label: string // e.g., "Name: Ex Pole 4"
}

interface GeojsonFileWrapper {
  file: File
  nameKey: string
  propertyOptions: PropertyOption[]
}

@Component({
  selector: 'shared-create-features-from-geojson',
  templateUrl: './create-features-from-geojson.component.html',
  styleUrls: ['./create-features-from-geojson.component.css'],
  standalone: false
})
export class CreateFeaturesFromGeojsonComponent {
  public geojsonFiles: GeojsonFileWrapper[] = []
  public touched: boolean = false
  public showDetails: boolean = true

  get isValid() { return this.geojsonFiles.length > 0 }
  get selectedSceneID() { return this.sceneService.selectedSceneID }

  constructor(
    public featureService: FeatureService,
    public sceneService: SceneService,
    public toastService: ToastService
  ) { }

  onFileChange(files: FileList) {
    Array.from(files).forEach(file => {
      const wrapper: GeojsonFileWrapper = { file, nameKey: '', propertyOptions: [] }
      this.geojsonFiles.push(wrapper)
      this.extractPropertyKeys(file, wrapper)
    })
    this.touched = true
  }

  replaceFile(file: File, index: number) {
    this.geojsonFiles[index].file = file
    this.extractPropertyKeys(file, this.geojsonFiles[index])
    this.touched = true
  }

  removeFile(index: number) {
    this.geojsonFiles.splice(index, 1)
    this.touched = true
  }

  private extractPropertyKeys(file: File, wrapper: GeojsonFileWrapper) {
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const content = reader.result as string
        const parsed = JSON.parse(content)

        let props: any = {}
        // For simplicity, just take the first feature if it's a FeatureCollection
        if (parsed.type === 'FeatureCollection' && parsed.features && parsed.features.length > 0) {
          props = parsed.features[0].properties
        } else if (parsed.type === 'Feature') {
          props = parsed.properties
        }

        if (props && typeof props === 'object') {
          // Build array of { key, label: "key: value" }
          wrapper.propertyOptions = Object.keys(props).map(key => {
            const value = props[key]
            return { key, label: `${key}: ${value}` }
          })
        }
      } catch (error) {
        console.error('Error extracting property keys from file:', file.name, error)
      }
    }
    reader.onerror = error => console.error('Error reading file', file.name, error)
    reader.readAsText(file)
  }

  showRequiredFields() {
    this.touched = true
  }

  // Main function to process files in sequence
  createFeaturesFromGeoJson$() {
    // Process each file one by one
    return from(this.geojsonFiles).pipe(
      concatMap(wrapper => 
        this.featureService.createFeaturesFromGeoJson(wrapper.file, this.selectedSceneID, { nameKey: wrapper.nameKey })
      ),
      toArray(),
      map(results => results.reduce((acc, curr) => acc.concat(curr), [] as Feature[]))
    )
  }
}